'use client'

import { type StorefrontWidget, type WidgetRelatedProducts } from 'ecosystem'
import RelatedProductsWidgetBase from 'storefront-modules/products/widgets/RelatedProductsWidgetBase'
import type { OverridesContextType } from 'ui/lib/overrides'
import { useGetProductsQuery } from 'shared-redux/state'
import useProductRequest from '#/src/custom/products/useProductRequest'
import { useCustomCart } from '#/src/custom/cart/useCustomCart'
import useAppConfig from '#/src/custom/hooks/useAppConfig'
import { hrefFromPathObj, isExternalLink } from 'shared-utils'
import { useTranslations } from 'next-intl'
import { CustomProductListItem } from '#/src/custom/products/CustomProductListItem'

interface RelatedProductsWidgetProps {
  widget: StorefrontWidget<WidgetRelatedProducts>
  overrides?: OverridesContextType
}

export const CustomRelatedProductsWidget = ({ widget }: RelatedProductsWidgetProps) => {
  const t = useTranslations('CustomRelatedProductsWidget')
  const appConfig = useAppConfig()
  const { data, isLoading } = useGetProductsQuery({
    filter: { productIds: widget.widget.relatedProducts.map((rp) => rp.id) }
  })
  const { handleProductRequest } = useProductRequest()
  const { handleAddToCart, cartItems } = useCustomCart()

  return (
    <RelatedProductsWidgetBase
      key={widget.widget.name}
      config={{
        title: widget.widget.title,
        ...(widget.widget.pathObj
          ? {
              headingLink: {
                title: t('showAll'),
                href: hrefFromPathObj(widget.widget.pathObj, {
                  category: appConfig.paths.categoriesUrlPath,
                  brand: appConfig.paths.brandsUrlPath,
                  tag: appConfig.paths.tagsUrlPath,
                  blog: appConfig.paths.blogsUrlPath
                }),
                isExternal: isExternalLink(widget.widget.pathObj.slug)
              }
            }
          : {}),
        relatedProducts: data?.content || [],
        onProductRequest: handleProductRequest,
        deliveryOptions: appConfig.storeDeliveries,
        onProductBuy: handleAddToCart,
        cartItems,
        paths: {
          productsUrlPath: appConfig.paths.productsUrlPath
        },
        itemCount: data?.content?.length || 0,
        isLoading,
        customComponents: {
          ProductListItem: CustomProductListItem
        }
      }}
    />
  )
}
